import {
  CodeExample,
  CodeExampleAttributeBased,
  CodeExampleProps,
  CodeExampleType,
} from "@/common/components/CodeExample";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { codeExamplesByUsecase } from "@/feature/data/codeExamplesByUsecase";
import { FeatureSourceType } from "@reflag/shared/featureAPI";

export type CodeExampleSDK = "react" | "node" | "browser";
export type CodeExampleUsecases =
  | "access"
  | "usage"
  | "feedback"
  | "config"
  | "monitor";

export type FeatureCodeExampleProps = Omit<
  CodeExampleProps,
  "examples" | "initialExample"
> & {
  feature: { key: string; id: string };
  featureSource?: FeatureSourceType;
  usecase: CodeExampleUsecases;
};

const sdkConfig: (Omit<CodeExampleType, "code"> & { key: CodeExampleSDK })[] = [
  {
    key: "react",
    label: "React SDK",
    highlighterLanguage: "jsx",
    docsSection: "supported-languages/react-sdk",
    template: "react",
  },
  {
    key: "browser",
    label: "Browser SDK",
    highlighterLanguage: "javascript",
    docsSection: "supported-languages/browser-sdk",
    template: "browser",
  },
  {
    key: "node",
    label: "Node.js SDK",
    highlighterLanguage: "javascript",
    docsSection: "supported-languages/node-sdk",
  },
];

export function FeatureCodeExample({
  feature,
  featureSource = "event",
  usecase = "access",
  ...rest
}: FeatureCodeExampleProps) {
  const { env } = useEnvironment();

  const examples = sdkConfig.map(({ key, ...rest }) => ({
    key,
    code: getCodeExampleByUsecase({
      featureKey: feature.key,
      publishableKey: env.sdkAccess.publishableKey,
      secretKey: env.sdkAccess.secretKey,
      language: key,
      usecase,
    }).trim(),
    ...rest,
  }));

  return featureSource === "event" ? (
    <CodeExample
      examples={examples}
      feature={feature}
      initialExample="react"
      {...rest}
    />
  ) : (
    <CodeExampleAttributeBased />
  );
}

function getCodeExampleByUsecase({
  featureKey,
  publishableKey,
  secretKey,
  language,
  usecase,
}: {
  featureKey: string;
  publishableKey: string;
  secretKey: string;
  language: CodeExampleSDK;
  usecase: CodeExampleUsecases;
}): string {
  return codeExamplesByUsecase(featureKey, publishableKey, secretKey)[language][
    usecase
  ];
}
